import { useEffect, useRef } from "react";
import { useMotionValueEvent } from "framer-motion";

export const clamp01 = (v) => Math.max(0, Math.min(1, v));

/**
 * useDrivenStyle - direct DOM style writes on scroll-progress change.
 * Framer's MotionValue→style pipeline flushes unreliably in this environment,
 * so scroll-linked styles are written imperatively instead.
 */
export function useDrivenStyle(progress, apply) {
  const ref = useRef(null);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useMotionValueEvent(progress, "change", (p) => {
    if (ref.current) applyRef.current(ref.current, p);
  });
  useEffect(() => {
    if (ref.current) applyRef.current(ref.current, progress.get());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

// range mapper: v in [a,b] → [from,to], clamped
export const mapRange = (v, a, b, from, to) => {
  const t = clamp01((v - a) / (b - a));
  return from + (to - from) * t;
};

// multi-stop mapper: stops + values (equal length, ascending stops)
export const mapStops = (v, stops, values) => {
  if (v <= stops[0]) return values[0];
  for (let i = 1; i < stops.length; i++) {
    if (v <= stops[i]) {
      const t = (v - stops[i - 1]) / (stops[i] - stops[i - 1]);
      return values[i - 1] + (values[i] - values[i - 1]) * t;
    }
  }
  return values[values.length - 1];
};

export const smoothstep = (t, a, b) => {
  const x = clamp01((t - a) / (b - a));
  return x * x * (3 - 2 * x);
};

export const lerp = (a, b, t) => a + (b - a) * t;

// easeInOutCubic for camera moves
export const easeInOut = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
